import { isAcTerminalId } from "./ac";
import type { DefLookup } from "./catalog";
import type { SimulateContext } from "./cable-current";
import { matchesAcSafetyIdentity } from "./diagnostics/ac-safety-identity";
import { buildGraph, v } from "./diagnostics/graph";
import { getStateOption } from "./simulate/states";
import type { ComponentDefinition, ComponentNode, System } from "./types";

/** The exact 2x120 V model charges from L1/N and accepts single-phase input
 * with L2 left open (§2.1 and Appendix §8.8). Electrical custom edits cannot
 * inherit that topology merely by retaining a catalog id.
 * https://www.victronenergy.com/media/pg/MultiPlus-II_2x120V/en/description.html
 */
export function hasMultiPlus2x120Input(node: ComponentNode, def: ComponentDefinition): boolean {
  return def.id === "shore-victron-multiplus-ii-12-3000-2x120" && matchesAcSafetyIdentity(node, def);
}

/** Trace L and N of one physical input to the same enabled source. Independent
 * transfer inputs cannot lend one another a missing conductor. L1/L2 inputs
 * may share the neutral explicitly declared under the same port prefix. */
export function hasActiveAcSupply(node: ComponentNode, def: ComponentDefinition, system: System, lookup: DefLookup, simulate: SimulateContext): boolean {
  const conductor = (id: string): "line" | "neutral" | undefined => {
    if (!isAcTerminalId(id)) return undefined;
    if (/(?:-|_)l\d?$/i.test(id)) return "line";
    if (/(?:-|_)n$/i.test(id)) return "neutral";
    return undefined;
  };
  const stem = (id: string) => id.replace(/[-_](?:l\d?|n)$/i, "").toLowerCase();
  const graph = buildGraph(system, lookup, { simulate, excludeSense: true });
  const sources = (terminalId: string, kind: "line" | "neutral"): Set<string> => {
    const queue = [v(node.id, terminalId)];
    const seen = new Set(queue), result = new Set<string>();
    for (let i = 0; i < queue.length; i++) {
      for (const { vertex } of graph.adj.get(queue[i]) ?? []) {
        const colon = vertex.lastIndexOf(":"), nodeId = vertex.slice(0, colon), termId = vertex.slice(colon + 1);
        if (conductor(termId) !== kind || seen.has(vertex)) continue;
        seen.add(vertex); queue.push(vertex);
        const source = system.nodes.find(n => n.id === nodeId);
        if (source?.kind !== "component") continue;
        const sourceDef = lookup(source.definitionId, source.customDefinition);
        if (sourceDef?.category !== "source") continue;
        const opt = getStateOption(sourceDef, simulate.componentStates[source.id]);
        if (opt && opt.id !== "off" && opt.ampOverride !== 0 && opt.ampMultiplier !== 0) result.add(source.id);
      }
    }
    return result;
  };
  const inputs = def.terminals.filter(t => t.direction === "input");
  // MSH3012RV HOT2 is pass-through only. Rev E §3.3 / troubleshooting p47
  // require HOT1 for charging; the common neutral must not make HOT2 alone
  // appear to power the charger. The exact MultiPlus-II 2x120 V also charges
  // from L1/N only (§2.1, linked above). Other split-phase/transfer devices
  // retain their existing input behavior.
  // https://www.sensata.com/sites/default/files/a/sensata-msh-3012rv-inverter-charger-manual.pdf
  const requiresL1 = ["shore-magnum-msh3012rv", "shore-magnum-msh3012rv-l"].includes(def.id) ||
    hasMultiPlus2x120Input(node, def);
  const chargingLines = inputs.filter(t => conductor(t.id) === "line" && (!requiresL1 || t.id === "ac-in-l1"));
  return chargingLines.some(line => {
    const neutral = inputs.find(t => conductor(t.id) === "neutral" && stem(t.id) === stem(line.id));
    if (!neutral) return false;
    const neutralSources = sources(neutral.id, "neutral");
    return [...sources(line.id, "line")].some(id => neutralSources.has(id));
  });
}
